import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import TextField from "components/form/textField/TextField";
import { PostEmailCampaignDraftTest } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import type { RootState } from "index";
import { useStore } from "react-redux";
import { showError, showSuccess } from "redux/actions/ui";
import { AppIsDevelopment } from "helpers/Helpers";
import { emailBuilderCommitAndFlushDraft } from "../../emailBuilderCommitAndFlushDraft";
import {
  EMAIL_BUILDER_MAX_TEST_RECIPIENTS,
  parseAndValidateEmailBuilderTestRecipients,
} from "./emailBuilderSendTestRecipients";
import "./emailBuilderSendTest.scss";
import { StatusCode } from "api/protocols";

interface IEmailBuilderSendTestSheetProps {
  open: boolean;
  onClose: () => void;
}

function apiErrorMessage(message: unknown, fallback: string): string {
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
}

export function EmailBuilderSendTestSheet(props: IEmailBuilderSendTestSheetProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const campaignId = useAppSelector(state => state.emailBuilderStore.campaign?.id ?? null);
  const signedInEmail = useAppSelector(store => {
    const email = store.authStore.user?.email;
    return typeof email === "string" ? email : "";
  });

  const [recipientsInput, setRecipientsInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [prevOpen, setPrevOpen] = useState(props.open);
  const isFlushingServerDraftRef = useRef(false);
  const hasSetInitialRecipientsRef = useRef(false);

  if (props.open !== prevOpen) {
    setPrevOpen(props.open);
    if (props.open) {
      if (!hasSetInitialRecipientsRef.current) {
        hasSetInitialRecipientsRef.current = true;
        if (!AppIsDevelopment() && signedInEmail.length > 0) {
          setRecipientsInput(signedInEmail);
        }
      }
      setValidationError(null);
      setSending(false);
    }
  }

  async function handleSend() {
    if (campaignId === null || sending) {
      return;
    }

    const validated = parseAndValidateEmailBuilderTestRecipients(recipientsInput, t);
    if (validated.ok === false) {
      setValidationError(validated.error);
      return;
    }

    setValidationError(null);
    setSending(true);

    try {
      const flushResult = await emailBuilderCommitAndFlushDraft({
        dispatch,
        getAutoSaveStatus: () => store.getState().emailBuilderStore.autoSaveStatus,
        getAutoSaveConflict: () => store.getState().emailBuilderStore.autoSaveConflict,
        isFlushing: isFlushingServerDraftRef.current,
        setFlushing: flushing => {
          isFlushingServerDraftRef.current = flushing;
        },
      });

      if (flushResult === "ignored" || flushResult === "failed") {
        return;
      }

      const res = await PostEmailCampaignDraftTest(
        campaignId,
        { recipients: validated.recipients },
        false,
      );

      if (res.status !== StatusCode.OK || res.data?.data == null) {
        dispatch(showError(apiErrorMessage(res.message, t("Could not send test email."))));
        return;
      }

      const count = res.data.data.recipient_count;
      dispatch(
        showSuccess(
          count === 1
            ? t("Test email queued for 1 recipient.")
            : t("Test email queued for {{count}} recipients.", { count }),
        ),
      );
      props.onClose();
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet
      open={props.open}
      size="small"
      title={t("Send test")}
      supportText={t("Send a test of the saved draft. Recipients will see a test marker.")}
      closable
      onCancel={props.onClose}
      cancelText={t("Cancel")}
      okText={t("Send test")}
      onOk={() => {
        void handleSend();
      }}
      okLoading={sending}
      okDisabled={sending || campaignId === null}
      cancelDisabled={sending}
      backDropCancel={!sending}
    >
      <div className="email-builder-send-test">
        <TextField
          label={t("Recipients")}
          helpText={t("Enter up to {{max}} email addresses, separated by commas or new lines.", {
            max: EMAIL_BUILDER_MAX_TEST_RECIPIENTS,
          })}
          placeholder={t("you@example.com, teammate@example.com")}
          value={recipientsInput}
          rows={4}
          error={validationError !== null}
          disabled={sending}
          onChange={event => {
            setRecipientsInput(event.target.value);
            if (validationError !== null) {
              setValidationError(null);
            }
          }}
        />
        {validationError !== null ? (
          <div className="email-builder-send-test-error">{validationError}</div>
        ) : null}
      </div>
    </Sheet>
  );
}
